// ============================================
// pages/reports/PayablesLedgerPage.tsx
// Unified payables ledger — one view of every outstanding amount MARKFED owes
// across the 5 payable types: farmer payments, transport, unloading, gunny, storage.
//
// The "credit-first" reality: procurement/services are consumed first, payments
// follow in partial installments. This report surfaces who's owed what, how long
// it's been outstanding, and flags overdue items.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { Wallet, Loader2, XCircle, Download, AlertTriangle } from 'lucide-react';
import {
  seasonsAPI,
  transportBillsAPI, unloadingBillsAPI, gunnyUtilizationAPI, storageBillsAPI,
  farmersAPI,
} from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

type ServiceType = 'farmer' | 'transport' | 'unloading' | 'gunny' | 'storage';

interface PayableRow {
  type: ServiceType;
  party: string;           // contractor / supplier / farmer / godown name
  ref: string;             // invoice / bill / PACS / receipt number
  ref_date: string;        // date used for aging
  district?: string;
  total_owed: number;
  paid: number;
  balance: number;
  aging_days: number;
  status: 'settled' | 'current' | 'pending' | 'overdue';
}

const TYPE_LABEL: Record<ServiceType, string> = {
  farmer: 'Farmer payment',
  transport: 'Transport bill',
  unloading: 'Unloading bill',
  gunny: 'Gunny vendor',
  storage: 'Storage charges',
};

const TYPE_COLOR: Record<ServiceType, string> = {
  farmer: 'bg-green-100 text-green-700',
  transport: 'bg-blue-100 text-blue-700',
  unloading: 'bg-indigo-100 text-indigo-700',
  gunny: 'bg-amber-100 text-amber-700',
  storage: 'bg-purple-100 text-purple-700',
};

const daysSince = (dateStr?: string): number => {
  if (!dateStr) return 0;
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  } catch {
    return 0;
  }
};

const bucketStatus = (balance: number, days: number): PayableRow['status'] => {
  if (balance <= 0) return 'settled';
  if (days < 30) return 'current';
  if (days < 60) return 'pending';
  return 'overdue';
};

export const PayablesLedgerPage: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [rows, setRows] = useState<PayableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [hideSettled, setHideSettled] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const s = await seasonsAPI.list();
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load seasons');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all 5 payable sources in parallel
        const [farmers, tBills, uBills, gUtil, sBills] = await Promise.all([
          farmersAPI.listAll(selectedSeasonId),
          transportBillsAPI.list(selectedSeasonId),
          unloadingBillsAPI.list(selectedSeasonId),
          gunnyUtilizationAPI.list(selectedSeasonId),
          storageBillsAPI.list(selectedSeasonId),
        ]);

        const out: PayableRow[] = [];

        // ─── Farmer payments (per district_farmers record) ──────────
        for (const f of (Array.isArray(farmers.data) ? farmers.data : []) as any[]) {
          const owed = num(f.cost_of_procured_qty_rs);
          const paid = num(f.payment_released_to_farmers_rs);
          if (owed === 0 && paid === 0) continue;
          const balance = Math.max(owed - paid, 0);
          const refDate = f.last_saved_at || f.submitted_at || f.created_at;
          const days = daysSince(refDate);
          out.push({
            type: 'farmer',
            party: f.district?.name || `District ${f.district_id}`,
            ref: `PACS: ${f.pacs_names || f.pacs_entity?.name || '--'}`,
            ref_date: refDate || '',
            district: f.district?.name,
            total_owed: owed, paid, balance, aging_days: days,
            status: bucketStatus(balance, days),
          });
        }

        // ─── Transport bills ────────────────────────────────────────
        for (const b of (Array.isArray(tBills.data) ? tBills.data : []) as any[]) {
          const owed = num(b.total_amount_verified) || num(b.total_amount_claimed);
          const paid = num(b.payment_released);
          const balance = Math.max(owed - paid - num(b.tds_amount), 0);
          const days = daysSince(b.bill_date || b.created_at);
          out.push({
            type: 'transport',
            party: b.contractor?.firm_name || `Contractor ${b.contractor_id}`,
            ref: b.bill_no || `Bill #${b.id}`,
            ref_date: b.bill_date || b.created_at || '',
            district: b.district?.name,
            total_owed: owed, paid, balance, aging_days: days,
            status: bucketStatus(balance, days),
          });
        }

        // ─── Unloading bills ────────────────────────────────────────
        for (const b of (Array.isArray(uBills.data) ? uBills.data : []) as any[]) {
          const owed = num(b.mgr_total) || num(b.dm_total) || num(b.total_bill_claimed);
          const paid = num(b.payment_released);
          const balance = Math.max(owed - paid - num(b.tds_amount), 0);
          const days = daysSince(b.bill_date || b.created_at);
          out.push({
            type: 'unloading',
            party: b.contractor?.firm_name || b.contractor?.contractor_name || `Contractor ${b.contractor_id}`,
            ref: b.invoice_no || `Bill #${b.id}`,
            ref_date: b.bill_date || b.created_at || '',
            district: b.district?.name || b.godown?.name,
            total_owed: owed, paid, balance, aging_days: days,
            status: bucketStatus(balance, days),
          });
        }

        // ─── Gunny utilization ──────────────────────────────────────
        for (const g of (Array.isArray(gUtil.data) ? gUtil.data : []) as any[]) {
          const owed = num(g.gross_amount);
          const paid = num(g.payment_released);
          const balance = Math.max(owed - paid, 0);
          const days = daysSince(g.supplier_invoice_date || g.created_at);
          out.push({
            type: 'gunny',
            party: g.supplier?.supplier_name || `Supplier ${g.supplier_id}`,
            ref: g.supplier_invoice_no || `Record #${g.id}`,
            ref_date: g.supplier_invoice_date || g.created_at || '',
            district: g.district?.name,
            total_owed: owed, paid, balance, aging_days: days,
            status: bucketStatus(balance, days),
          });
        }

        // ─── Storage bills ──────────────────────────────────────────
        for (const s of (Array.isArray(sBills.data) ? sBills.data : []) as any[]) {
          const owed = num(s.total_payable) || num(s.net_amount);
          const paid = num(s.payment_released);
          const balance = Math.max(owed - paid - num(s.tds_amount), 0);
          const days = daysSince(s.bill_date || s.created_at);
          out.push({
            type: 'storage',
            party: s.godown?.godown_name || `Godown ${s.godown_id}`,
            ref: s.bill_no || `SB #${s.id}`,
            ref_date: s.bill_date || s.created_at || '',
            district: undefined,
            total_owed: owed, paid, balance, aging_days: days,
            status: bucketStatus(balance, days),
          });
        }

        // Sort: outstanding balances first, biggest balance at top
        out.sort((a, b) => b.balance - a.balance);
        setRows(out);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load payables');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (hideSettled && r.status === 'settled') return false;
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      return true;
    });
  }, [rows, hideSettled, typeFilter, statusFilter]);

  const totals = useMemo(() => {
    const allRows = rows.filter((r) => r.status !== 'settled');
    return {
      count: allRows.length,
      total_owed: allRows.reduce((s, r) => s + r.total_owed, 0),
      paid: allRows.reduce((s, r) => s + r.paid, 0),
      balance: allRows.reduce((s, r) => s + r.balance, 0),
      current: allRows.filter((r) => r.status === 'current').reduce((s, r) => s + r.balance, 0),
      pending: allRows.filter((r) => r.status === 'pending').reduce((s, r) => s + r.balance, 0),
      overdue: allRows.filter((r) => r.status === 'overdue').reduce((s, r) => s + r.balance, 0),
    };
  }, [rows]);

  const statusBadge = (s: PayableRow['status']) => {
    switch (s) {
      case 'settled': return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">✓ settled</span>;
      case 'current': return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">current</span>;
      case 'pending': return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700">pending</span>;
      case 'overdue': return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">overdue</span>;
    }
  };

  const handlePrint = () => window.print();

  if (loading && rows.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto print:max-w-none">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Wallet className="w-7 h-7 text-blue-600" />
            Payables Ledger
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Outstanding balances across farmer payments, transport, unloading, gunny, and storage — with aging
          </p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-lg text-sm">
          <Download className="w-4 h-4" />Print
        </button>
      </div>

      {/* Aging summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Total Outstanding</p>
          <p className="text-2xl font-bold text-gray-900">{formatAmount(totals.balance)}</p>
          <p className="text-[10px] text-gray-400 mt-1">{totals.count} open records</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-green-200 p-4">
          <p className="text-xs text-gray-500">Current (&lt; 30 days)</p>
          <p className="text-2xl font-bold text-green-700">{formatAmount(totals.current)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-yellow-200 p-4">
          <p className="text-xs text-gray-500">Pending (30-60 days)</p>
          <p className="text-2xl font-bold text-yellow-700">{formatAmount(totals.pending)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />Overdue (60+ days)
          </p>
          <p className="text-2xl font-bold text-red-700">{formatAmount(totals.overdue)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4 print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Service Type</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="all">All types</option>
              <option value="farmer">Farmer payments</option>
              <option value="transport">Transport bills</option>
              <option value="unloading">Unloading bills</option>
              <option value="gunny">Gunny vendors</option>
              <option value="storage">Storage charges</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="all">All statuses</option>
              <option value="current">Current</option>
              <option value="pending">Pending (30-60d)</option>
              <option value="overdue">Overdue (60+d)</option>
              <option value="settled">Settled</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
            <input type="checkbox" checked={hideSettled} onChange={(e) => setHideSettled(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
            Hide settled
          </label>
        </div>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" />{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Party</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Reference</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">District</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Owed</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Paid</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Balance</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Aging</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">No payables match these filters.</td></tr>
              ) : filtered.map((r, i) => (
                <tr key={`${r.type}-${i}`} className={`hover:bg-gray-50 ${r.status === 'overdue' ? 'bg-red-50/30' : ''}`}>
                  <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${TYPE_COLOR[r.type]}`}>
                      {TYPE_LABEL[r.type]}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">{r.party}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-[11px]">{r.ref}</td>
                  <td className="px-3 py-2 text-gray-700">{r.district || '--'}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(r.total_owed)}</td>
                  <td className="px-3 py-2 text-right text-green-700">{formatAmount(r.paid)}</td>
                  <td className="px-3 py-2 text-right font-bold text-orange-700">{formatAmount(r.balance)}</td>
                  <td className={`px-3 py-2 text-right ${r.aging_days > 60 ? 'text-red-600 font-semibold' : r.aging_days > 30 ? 'text-yellow-700' : 'text-gray-600'}`}>
                    {r.aging_days}d
                  </td>
                  <td className="px-3 py-2 text-center">{statusBadge(r.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500 italic">
        Aging is computed from the invoice / reference date on each record.
        <span className="text-green-700 ml-2">Current &lt; 30 days</span> ·
        <span className="text-yellow-700 ml-2">Pending 30-60 days</span> ·
        <span className="text-red-700 ml-2">Overdue 60+ days</span>.
        Transport and unloading balances deduct TDS withheld.
      </div>
    </div>
  );
};

export default PayablesLedgerPage;
